import { useState } from "react";
import { Check, ArrowRight, ChevronDown } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { PLANS } from "@/data/content";
import { useLead } from "@/context/LeadContext";

// Map a team size to the right seat-based tier.
const tierFor = (agents) => {
  if (agents <= 1)
    return { name: "Solo Agent", price: "$49", cadence: "/mo", blurb: "Everything one agent needs to win listings.", seats: "1 seat" };
  if (agents <= 5)
    return { name: "Broker Starter", price: "$149", cadence: "/mo", blurb: "For boutique teams standardizing their marketing.", seats: "up to 5 seats" };
  if (agents <= 15)
    return { name: "Broker Pro", price: "$299", cadence: "/mo", blurb: "For growth teams that need ROI visibility per agent.", seats: "up to 15 seats" };
  return { name: "Agency Enterprise", price: "$599", cadence: "/mo", blurb: "For large agencies with dedicated support.", seats: "up to 50 seats" };
};

export const Pricing = () => {
  const { openLead } = useLead();
  const [agents, setAgents] = useState(1);
  const [showAll, setShowAll] = useState(false);
  const tier = tierFor(agents);

  return (
    <section id="pricing" className="py-24 md:py-32 border-t border-ink/10" data-testid="pricing-section">
      <div className="mx-auto max-w-[1000px] px-5 sm:px-8">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.2em] text-coral mb-4">Pricing</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-ink max-w-3xl">
            Simple, seat-based pricing. No per-listing fees.
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-14 border border-ink/15 bg-bone p-8 md:p-12" data-testid="pricing-card">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              {/* Live tier */}
              <div>
                <p className="text-sm uppercase tracking-[0.12em] text-ink/50">{tier.seats}</p>
                <motion.div
                  key={tier.name}
                  initial={{ opacity: 0.5, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <h3 className="mt-2 font-serif text-4xl md:text-5xl text-ink" data-testid="pricing-tier-name">
                    {tier.name}
                  </h3>
                  <div className="mt-3 flex items-end gap-1">
                    <span className="font-serif text-6xl md:text-7xl leading-none tracking-tight text-ink" data-testid="pricing-tier-price">
                      {tier.price}
                    </span>
                    <span className="text-base mb-2 text-ink/50">{tier.cadence}</span>
                  </div>
                </motion.div>
                <p className="mt-4 text-sm text-ink/60 max-w-sm">{tier.blurb}</p>

                <button
                  onClick={() => openLead({ plan: tier.name, source: "pricing" })}
                  data-testid="pricing-cta"
                  className="group mt-8 inline-flex items-center gap-2 bg-coral text-bone px-7 py-4 text-sm font-medium hover:bg-ink transition-colors duration-300"
                >
                  Start free — no credit card
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>
              </div>

              {/* Slider */}
              <div>
                <label className="block text-sm text-ink/60 mb-4">
                  How many agents on your team?{" "}
                  <span className="text-ink font-medium">{agents}</span>
                </label>
                <input
                  type="range"
                  min="1"
                  max="50"
                  value={agents}
                  onChange={(e) => setAgents(Number(e.target.value))}
                  data-testid="pricing-slider"
                  className="w-full accent-coral h-2 cursor-pointer"
                  aria-label="Number of agents"
                />
                <div className="flex justify-between text-xs text-ink/40 mt-2">
                  <span>1</span><span>5</span><span>15</span><span>50</span>
                </div>
                <p className="mt-6 text-xs text-ink/50 leading-relaxed">
                  All plans include unlimited virtual AI agents, native 360° tour capture, AI virtual
                  staging, Meta + Google ads, RentCast market data, public listing pages, and
                  QR-linked digital business cards.
                </p>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Expandable full comparison */}
        <Reveal delay={0.15}>
          <button
            onClick={() => setShowAll((v) => !v)}
            data-testid="pricing-compare-toggle"
            className="mt-6 inline-flex items-center gap-2 text-sm text-ink border-b border-coral pb-0.5 hover:text-coral transition-colors"
          >
            Compare all features
            <ChevronDown className={`w-4 h-4 transition-transform ${showAll ? "rotate-180" : ""}`} />
          </button>

          <AnimatePresence initial={false}>
            {showAll && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden"
                data-testid="pricing-compare-panel"
              >
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                  {PLANS.map((p) => (
                    <div
                      key={p.name}
                      data-testid={`pricing-card-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`relative h-full flex flex-col p-6 border ${
                        p.popular ? "border-coral bg-ink text-bone" : "border-ink/15 bg-bone text-ink"
                      }`}
                    >
                      {p.popular && (
                        <span className="absolute -top-3 left-6 bg-coral text-bone text-[10px] uppercase tracking-[0.15em] px-3 py-1">
                          Most popular
                        </span>
                      )}
                      <h4 className={`text-sm uppercase tracking-[0.12em] ${p.popular ? "text-bone/70" : "text-ink/60"}`}>
                        {p.name}
                      </h4>
                      <div className="mt-4 flex items-end gap-1">
                        <span className="font-serif text-4xl leading-none tracking-tight">{p.price}</span>
                        <span className={`text-sm mb-1 ${p.popular ? "text-bone/60" : "text-ink/50"}`}>{p.cadence}</span>
                      </div>
                      <p className={`mt-2 text-xs ${p.popular ? "text-bone/60" : "text-ink/50"}`}>{p.for}</p>
                      <ul className="mt-5 space-y-2.5 flex-1">
                        {p.included.map((f) => (
                          <li key={f} className="flex items-start gap-2 text-sm">
                            <Check className="w-4 h-4 mt-0.5 shrink-0 text-coral" />
                            <span className={p.popular ? "text-bone/80" : "text-ink/70"}>{f}</span>
                          </li>
                        ))}
                      </ul>
                      <button
                        onClick={() => openLead({ plan: p.name, source: "pricing-compare" })}
                        data-testid={`pricing-cta-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`mt-6 group inline-flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors duration-300 ${
                          p.popular ? "bg-coral text-bone hover:bg-bone hover:text-ink" : "bg-ink text-bone hover:bg-coral"
                        }`}
                      >
                        Start free
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </button>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <a
            href="mailto:hello@chateaux.ai"
            data-testid="pricing-custom-cta"
            className="mt-8 block text-sm text-ink/60 hover:text-coral transition-colors"
          >
            Custom seat counts? → hello@chateaux.ai
          </a>
        </Reveal>
      </div>
    </section>
  );
};
